import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  VStack,
  FormHelperText,
  Alert,
  AlertIcon,
  AlertDescription,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  useColorModeValue,
  HStack,
  Switch,
} from '@chakra-ui/react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { FiChevronDown } from 'react-icons/fi'
import { AdminUser } from '@/types'
import {
  useAdminRoles,
  useCreateAdminUser,
  useUpdateAdminUser,
  useUpdateAdminUserStatus,
} from '@/hooks/useAdminData'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/useToast'
import { TEXT_CONSTRAINTS } from '@shared/inputConstraints'
import { validatePasswordStrength } from '@shared/passwordStrength'
import { validateTextWithConstraint } from '@shared/textConstraintHelpers'
import { getAssignableAdminRoles } from './adminRoleOptions'
import {
  hasAdminProfileChanges,
  hasAdminStatusChange,
} from './adminUserEditChanges'
import { resolveApiErrorMessage } from '@/services/admin/baseService'

const usernameConstraint = TEXT_CONSTRAINTS['user.name']
const emailConstraint = TEXT_CONSTRAINTS['user.email']
const passwordConstraint = TEXT_CONSTRAINTS['user.password']

const preprocessEmail = (value: unknown): unknown => {
  if (typeof value !== 'string') return value

  let processed = value

  if (emailConstraint.trim !== false) {
    processed = processed.trim()
  }

  if (emailConstraint.transform === 'lowercase') {
    processed = processed.toLowerCase()
  }

  return processed
}

const preprocessUsername = (value: unknown): unknown => {
  if (typeof value !== 'string') return value
  return value.trim()
}

const adminUserSchema = z.object({
  email: z.preprocess(
    preprocessEmail,
    z
      .string()
      .min(emailConstraint.minLength ?? 6, 'Invalid email address')
      .max(
        emailConstraint.maxLength ?? 254,
        `Email must not exceed ${emailConstraint.maxLength ?? 254} characters`
      )
      .regex(emailConstraint.pattern!, 'Invalid email address')
  ),
  username: z.preprocess(
    preprocessUsername,
    z
      .string()
      .min(
        usernameConstraint.minLength ?? 3,
        `Username must be at least ${usernameConstraint.minLength ?? 3} characters`
      )
      .max(
        usernameConstraint.maxLength ?? 32,
        `Username must be at most ${usernameConstraint.maxLength ?? 32} characters`
      )
      .regex(
        usernameConstraint.pattern!,
        'Username can only contain letters, numbers, underscores, and hyphens'
      )
  ),
  password: z
    .preprocess(
      (value) => (typeof value === 'string' && value.length === 0 ? undefined : value),
      z
        .string()
        .min(
          passwordConstraint.minLength ?? 8,
          `Password must be at least ${passwordConstraint.minLength ?? 8} characters`
        )
        .max(
          passwordConstraint.maxLength ?? 128,
          `Password must be at most ${passwordConstraint.maxLength ?? 128} characters`
        )
        .superRefine((password, context) => {
          const strength = validatePasswordStrength(password)
          if (!strength.valid) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: strength.errors[0] ?? 'Password does not meet complexity requirements',
            })
          }
        })
        .optional()
    ),
  role_id: z.string().min(1, 'Role is required'),
  is_active: z.boolean(),
})

type AdminUserFormData = z.infer<typeof adminUserSchema>

interface AdminUserModalProps {
  isOpen: boolean
  onClose: () => void
  user?: AdminUser | null
  onSuccess?: () => void
}

export function AdminUserModal({ isOpen, onClose, user, onSuccess }: AdminUserModalProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const { user: currentUser } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const placeholderColor = useColorModeValue('gray.500', 'gray.400')
  
  const { data: rolesData, isLoading: rolesLoading } = useAdminRoles({
    enabled: isOpen,
  })
  const createMutation = useCreateAdminUser()
  const updateMutation = useUpdateAdminUser()
  const statusMutation = useUpdateAdminUserStatus()
  
  const isEditMode = !!user
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  } = useForm<AdminUserFormData>({
    resolver: zodResolver(adminUserSchema),
    defaultValues: {
      email: '',
      username: '',
      password: '',
      role_id: '',
      is_active: true,
    },
  })

  useEffect(() => {
    if (user) {
      setValue('email', user.email)
      setValue('username', user.username)
      setValue('role_id', user.role_id || user.role_code || '')
      setValue('is_active', user.is_active)
    } else {
      reset()
    }
  }, [user, setValue, reset])

  const onSubmit = async (data: AdminUserFormData) => {
    setIsSubmitting(true)
    
    try {
      const emailCheck = validateTextWithConstraint(data.email, emailConstraint, { requiredOverride: true })
      const usernameCheck = validateTextWithConstraint(data.username, usernameConstraint, { requiredOverride: true })

      if (!emailCheck.valid || !emailCheck.value) {
        toast.error({ titleKey: 'adminUsers.validation.emailInvalid' })
        setIsSubmitting(false)
        return
      }

      if (!usernameCheck.valid || !usernameCheck.value) {
        toast.error({ titleKey: 'adminUsers.validation.usernameInvalid' })
        setIsSubmitting(false)
        return
      }

      const sanitizedEmail = emailCheck.value
      const sanitizedUsername = usernameCheck.value

      if (isEditMode && user) {
        const updateData = {
          email: sanitizedEmail,
          username: sanitizedUsername,
          role_id: data.role_id,
        }

        if (hasAdminProfileChanges(user, updateData)) {
          await updateMutation.mutateAsync({
            userId: user.id,
            data: updateData,
          })
        }

        if (hasAdminStatusChange(user, data.is_active)) {
          await statusMutation.mutateAsync({
            userId: user.id,
            data: {
              is_active: data.is_active,
              reason: data.is_active
                ? 'Enabled from Edit Admin User'
                : 'Disabled from Edit Admin User',
            },
          })
        }

        toast.success({
          titleKey: 'adminUsers.updateSuccess'
        })
      } else {
        if (!data.password) {
          toast.error({
            titleKey: 'adminUsers.passwordRequired'
          })
          setIsSubmitting(false)
          return
        }

        const passwordCheck = validateTextWithConstraint(data.password, passwordConstraint, { requiredOverride: true })

        if (!passwordCheck.valid || !passwordCheck.value) {
          toast.error({ titleKey: 'adminUsers.validation.passwordInvalid' })
          setIsSubmitting(false)
          return
        }
        
        await createMutation.mutateAsync({
          email: sanitizedEmail,
          username: sanitizedUsername,
          password: passwordCheck.value,
          role_id: data.role_id,
        })
        
        toast.success({
          titleKey: 'adminUsers.createSuccess'
        })
      }
      
      onSuccess?.()
      onClose()
      reset()
    } catch (error: unknown) {
      toast.error({
        titleKey: isEditMode ? 'adminUsers.updateError' : 'adminUsers.createError',
        description: resolveApiErrorMessage(error, t('common.unknownError')),
        duration: 6000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // Safely get available roles with proper error handling
  const availableRoles = getAssignableAdminRoles(rolesData?.data, currentUser)
  const cannotEditOwnRole = isEditMode && user?.id === currentUser?.id
  const cannotEditOwnStatus = isEditMode && user?.id === currentUser?.id
  const hasRolesError = !rolesLoading && rolesData && !rolesData.success

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEditMode ? t('adminUsers.editTitle') : t('adminUsers.createTitle')}
        </ModalHeader>
        <ModalCloseButton />
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <VStack spacing={4}>
              {cannotEditOwnRole && (
                <Alert status="warning">
                  <AlertIcon />
                  <AlertDescription>
                    {t('adminUsers.cannotEditOwnRole')}
                  </AlertDescription>
                </Alert>
              )}
              
              <FormControl isInvalid={!!errors.email}>
                <FormLabel>{t('adminUsers.form.email')}</FormLabel>
              <Input
                type="email"
                placeholder={t('adminUsers.form.emailPlaceholder')}
                maxLength={emailConstraint.maxLength}
                {...register('email')}
              />
                <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.username}>
                <FormLabel>{t('adminUsers.form.username')}</FormLabel>
              <Input
                placeholder={t('adminUsers.form.usernamePlaceholder')}
                maxLength={usernameConstraint.maxLength}
                {...register('username')}
              />
                <FormErrorMessage>{errors.username?.message}</FormErrorMessage>
              </FormControl>

              {!isEditMode && (
                <FormControl isInvalid={!!errors.password} isRequired>
                  <FormLabel>{t('adminUsers.form.password')}</FormLabel>
                  <Input
                    type="password"
                    placeholder={t('adminUsers.form.passwordPlaceholder')}
                    maxLength={passwordConstraint.maxLength}
                    {...register('password')}
                  />
                  <FormHelperText>{t('adminUsers.form.passwordHelp')}</FormHelperText>
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>
              )}

              {isEditMode && (
                <FormControl isDisabled={cannotEditOwnStatus}>
                  <HStack justify="space-between" width="100%">
                    <FormLabel mb={0}>{t('adminUsers.form.status')}</FormLabel>
                    <Controller
                      name="is_active"
                      control={control}
                      render={({ field }) => (
                        <HStack spacing={3}>
                          <Text fontSize="sm">
                            {field.value
                              ? t('adminUsers.status.active')
                              : t('adminUsers.status.inactive')}
                          </Text>
                          <Switch
                            colorScheme="green"
                            isChecked={field.value}
                            onChange={event => field.onChange(event.target.checked)}
                          />
                        </HStack>
                      )}
                    />
                  </HStack>
                </FormControl>
              )}

              <FormControl isInvalid={!!errors.role_id} isDisabled={cannotEditOwnRole}>
                <FormLabel>{t('adminUsers.form.role')}</FormLabel>
                <Controller
                  name="role_id"
                  control={control}
                  render={({ field }) => {
                    const selectedRole = rolesData?.data?.find(role => role.id === field.value)

                    return (
                      <Menu matchWidth>
                        <MenuButton
                          as={Button}
                          rightIcon={<FiChevronDown />}
                          variant="outline"
                          width="100%"
                          fontWeight="normal"
                          textAlign="left"
                          isDisabled={rolesLoading || cannotEditOwnRole || hasRolesError}
                        >
                          <Text
                            as="span"
                            display="block"
                            color={selectedRole ? undefined : placeholderColor}
                            noOfLines={1}
                          >
                            {selectedRole?.name ?? t('adminUsers.form.rolePlaceholder')}
                          </Text>
                        </MenuButton>
                        <MenuList maxH="260px" overflowY="auto" zIndex={1600}>
                          {availableRoles.map((role) => (
                            <MenuItem key={role.id} onClick={() => field.onChange(role.id)}>
                              {role.name}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    )
                  }}
                />
                <FormErrorMessage>{errors.role_id?.message}</FormErrorMessage>
                {hasRolesError && (
                  <FormHelperText color="red.500">
                    {t('adminUsers.form.rolesLoadError')}
                  </FormHelperText>
                )}
                {!hasRolesError && availableRoles.length === 0 && !rolesLoading && (
                  <FormHelperText color="orange.500">
                    {t('adminUsers.form.noRolesAvailable')}
                  </FormHelperText>
                )}
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              isLoading={isSubmitting}
              isDisabled={
                (availableRoles.length === 0 && !isEditMode)
                || hasRolesError
                || statusMutation.isPending
              }
            >
              {isEditMode ? t('common.update') : t('common.create')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
